import { Injectable, signal, computed, inject } from "@angular/core"; // Import signal and computed
import { Subject } from "rxjs";
import { S25EventUtil } from "../utils/s25.event.util";
import { EventService } from "../../../services/event.service";
import { EventMicroService } from "../../../services/event.micro.service";
import { S25Util } from "../../../util/s25-util";
import { EventMicroI } from "../EventMicroI";
import { CacheRepository } from "../../../decorators/cache.decorator";
import { S25Event } from "../EventMicroI";
import { S25Profile } from "../ProfileI";
import { WaitlistStore } from "../../../stores/waitlist.store";
import { WaitlistEntry, WaitingEntry } from "../../../pojo/Waitlist";
import { BpeService } from "../../bpe/bpe.service";
import { EventData } from "../../../services/event.service";
import {
    runDeferredOccurrenceInlineEditScenarios,
    runOccurrenceInlineEditScenarios,
} from "./occurrence.inline.edit.bpe.util";

@Injectable()
export class S25EventOccurrencesService {
    private waitlistStore = inject(WaitlistStore);
    // Define a signal to hold the event state
    public S25Event = signal<S25Event | null>(null);

    /** When true, occurrence inline saves skip event save email scenarios. */
    readonly silentSave = signal(true);

    /** Event data captured when silent save is enabled, before batch edits. */
    readonly deferredScenarioPreData = signal<Promise<EventData | undefined> | undefined>(undefined);

    /** True after at least one silent save since deferredScenarioPreData was captured. */
    readonly hasPendingScenarioChanges = signal(false);

    runInlineEditScenarios<T>(eventId: number, change: () => Promise<T>): Promise<T> {
        const silent = this.silentSave();
        if (silent) {
            this.hasPendingScenarioChanges.set(true);
        }
        return runOccurrenceInlineEditScenarios(eventId, change, silent);
    }

    captureDeferredScenarioPreData(eventId: number, eventData?: EventData): void {
        this.deferredScenarioPreData.set(eventData ? Promise.resolve(eventData) : BpeService.getEventData(eventId));
    }

    clearDeferredScenarioState(): void {
        this.deferredScenarioPreData.set(undefined);
        this.hasPendingScenarioChanges.set(false);
    }

    async runDeferredInlineEditScenarios(eventId: number): Promise<void> {
        const preDataPromise = this.deferredScenarioPreData();
        if (!preDataPromise || !this.hasPendingScenarioChanges()) {
            return;
        }
        const preEventData = await preDataPromise;
        if (!preEventData) {
            return;
        }
        const postEventData = await runDeferredOccurrenceInlineEditScenarios(eventId, preEventData);
        this.hasPendingScenarioChanges.set(false);
        this.captureDeferredScenarioPreData(eventId, postEventData);
    }

    public getOccurrences = computed(() => {
        const event = this.S25Event();
        return event ? S25EventUtil.getAllOccurrences(event) : [];
    });

    public getProfiles = computed(() => this.S25Event()?.profile ?? []);

    // Add a new profile to the current event state
    addProfile(newProfile: S25Profile) {
        const event = this.S25Event();
        if (event) {
            const updatedEvent = S25EventUtil.addProfile(event, newProfile);
            this.S25Event.set(updatedEvent); // Ensure the signal is updated after modification
            return updatedEvent;
        }
        console.warn("Attempted to add profile to a non-existent event.");
        return null;
    }

    deleteTempProfile(profileId: number) {
        const event = this.S25Event();
        if (event) {
            event.profile = event.profile?.filter((profile) => profile.itemId !== profileId);
            this.S25Event.set(event);
            return event;
        }
    }

    private _refreshRequested = new Subject<void>();
    readonly refreshRequested$ = this._refreshRequested.asObservable();

    requestRefresh() {
        this._refreshRequested.next();
    }

    constructor() {}

    // Fetch the event and set it in the signal
    async getEvent(eventId: number, force?: boolean, newUpdateData?: EventMicroI) {
        let taskService = EventService.getEventIncludeCached;
        if (force) {
            CacheRepository.invalidateByService("EventMicroService", "getEventDetailById");
            taskService = EventService.getEventInclude;
        }

        try {
            const data: EventMicroI =
                newUpdateData ||
                (await EventMicroService.getEventDetailById(S25Util.array.forceArray(eventId), true, ["all"]));
            data.content.approval = await taskService(eventId, ["workflow"]);
            const event = await S25EventUtil.setFromWS(data.content);
            this.S25Event.set(event);
            await this.fetchWaitlistEntriesForEvent(force);
            return this.S25Event();
        } catch (error) {
            console.error("Error fetching event:", error);
            throw error;
        }
    }

    /// Occurrences Expand All/ Collapse All
    private _expandAll = signal<boolean>(false);

    setExpandAll(showDetail: boolean) {
        this._expandAll.set(showDetail);
    }

    getExpandAll = computed(() => this._expandAll());

    // set caller tag use in Expand All/ Collapse All
    private _callerTags = signal<string>("");

    setCallerTags(tags: string) {
        this._callerTags.set(tags);
    }

    getCallerTags = computed(() => this._callerTags());

    /**
     * Fetch waitlist entries for all confirmed reservations in the current event.
     * Uses GET /waitlist/event.json for the current event.
     *
     * @returns Promise that resolves when all waitlist entries are loaded
     */
    async fetchWaitlistEntriesForEvent(force?: boolean): Promise<void> {
        const event = this.S25Event();
        if (!event || !event.itemId) {
            return;
        }

        try {
            const allEventGroups = await this.waitlistStore.getWaitlistEntries(
                { event_id: event.itemId, include_requests: "all_competes" },
                force,
            );

            if (!allEventGroups?.length) return;

            const blockingEntries = allEventGroups;
            const eventByIdEntries = allEventGroups;

            /* -----------------------------------
                    Group by reservation (event_id)
                    ----------------------------------- */
            const waitlistRsrvMap = new Map<number, WaitlistEntry[]>();
            for (const entry of eventByIdEntries) {
                for (const w of entry.waiting ?? []) {
                    if (!waitlistRsrvMap.has(w.rsrvId)) {
                        waitlistRsrvMap.set(w.rsrvId, []);
                    }
                    waitlistRsrvMap.get(w.rsrvId)!.push(entry);
                }
            }

            /* -----------------------------------
                 get event waiting -   Group by blocking reservation
           ----------------------------------- */
            const waitlistBlockingMap = new Map<number, WaitlistEntry[]>();

            for (const entry of blockingEntries) {
                if (!entry.blockingRsrvId) continue;

                if (!waitlistBlockingMap.has(entry.blockingRsrvId)) {
                    waitlistBlockingMap.set(entry.blockingRsrvId, []);
                }
                waitlistBlockingMap.get(entry.blockingRsrvId)!.push(entry);
            }

            /* -----------------------------------
                    Update event occurrences
                    ----------------------------------- */
            const updatedEvent = { ...event };

            updatedEvent.profile = updatedEvent.profile?.map((profile) => ({
                ...profile,
                reservations: profile.reservations?.map((occ) => {
                    if (!("itemId" in occ) || !occ.itemId) return occ;

                    return {
                        ...occ,
                        waitlistEntries: waitlistBlockingMap.get(occ.itemId),
                        waitlistRsrvEntries: waitlistRsrvMap.get(occ.itemId),
                    };
                }),
            }));

            this.S25Event.set(updatedEvent);
        } catch (error) {
            console.warn(`Failed to fetch waitlist entries for event ${event.itemId}:`, error);
        }
    }

    updateProfileAddedTime(
        profileId: number,
        addedTime: { setup?: string; takedown?: string; preEvent?: string; postEvent?: string },
    ) {
        const event = this.S25Event();
        if (event) {
            const profile = event.profile?.find((p) => p.itemId === profileId);
            if (profile?.occurrenceDefn) {
                profile.occurrenceDefn.addedTime = {
                    ...profile.occurrenceDefn.addedTime,
                    ...addedTime,
                };
                this.S25Event.set({ ...event }); // Trigger signal update
            }
        }
    }
}
